const MAX_DIMENSION = 10_000
const SKIP_THRESHOLD = 500 * 1024 // 500KB

interface CompressOptions {
  maxWidth?: number
  maxHeight?: number
}

const DEFAULTS: Required<CompressOptions> = {
  maxWidth: 5000,
  maxHeight: 5000,
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Failed to load image'))
    img.src = src
  })
}

function calcDimensions(
  w: number,
  h: number,
  maxW: number,
maxH: number
): { width: number; height: number } {
  if (w <= maxW && h <= maxH) return { width: w, height: h }

  const ratio = Math.min(maxW / w, maxH / h)
  return {
    width: Math.round(w * ratio),
    height: Math.round(h * ratio),
  }
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number
): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality))
}

export async function compressImage(
  file: File,
  options?: CompressOptions
): Promise<File> {
  // Skip if already small WebP
  if (file.type === 'image/webp' && file.size <= SKIP_THRESHOLD) {
    return file
  }

  const { maxWidth, maxHeight } = { ...DEFAULTS, ...options }

  const url = URL.createObjectURL(file)
  try {
    const img = await loadImage(url)

    // Safety: reject decompression bombs
    if (img.naturalWidth > MAX_DIMENSION || img.naturalHeight > MAX_DIMENSION) {
      throw new Error(
        `Изображение слишком большое: ${img.naturalWidth}×${img.naturalHeight}px (макс. ${MAX_DIMENSION}px)`
      )
    }

    const { width, height } = calcDimensions(
      img.naturalWidth,
      img.naturalHeight,
      maxWidth,
      maxHeight
    )

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height

    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Canvas 2D context unavailable')

    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(img, 0, 0, width, height)

    // Lossless WebP: quality must be exactly 1 — any value < 1 selects the
    // lossy encoder (Chromium), which shifts pixel colors. Fall back to PNG
    // (also lossless) if WebP encoding is unsupported.
    let blob = await canvasToBlob(canvas, 'image/webp', 1)
    let ext = 'webp'
    let mime = 'image/webp'

    if (!blob) {
      blob = await canvasToBlob(canvas, 'image/png', 1)
      ext = 'png'
      mime = 'image/png'
    }

    if (!blob) throw new Error('Failed to compress image')

    const baseName = file.name.replace(/\.[^.]+$/, '')
    return new File([blob], `${baseName}.${ext}`, { type: mime })
  } finally {
    URL.revokeObjectURL(url)
  }
}
